import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { refreshStockLevels, useStockLevels } from '../../store/stockLevels';
import { StockChip } from '../stock/StockChip';
import { RestockConfirmDialog } from './RestockConfirmDialog';
import { requireSavedHeld } from '../held-orders/tableActions';
import { POS_PERMISSIONS } from '@matix/types';
import { salesmenAPI } from '@matix/api-client';
import { useCart, useCartTotals, lineDiscountAmount } from '../../store/cart';
import type { CartLine } from '../../store/cart';
import { amountStep, amountText, moneyPrecision } from '../../store/currency';
import { roundMoney } from '@matix/utils';
import { useUI } from '../../store/ui';
import { useCanI } from '../../lib/permissions';
import { usePriceChangeRule, isPriceChangeAllowed } from '../../lib/priceChangeRule';
import { useAuth } from '@matix/auth';
import { useCustomerPicker } from '../../store/customers';
import { useTaxConfig } from '../../store/taxConfig';
import { useModuleOn, useTenantConfig } from '../../store/tenantConfig';
import { CustomerMeasurementsModal } from '../customers/CustomerMeasurementsModal';
import { ReadyByDialog, useLaundryPrint } from '../laundry';
import { useLoyalty } from '../../store/loyalty';
import { useGiftCards } from '../../store/giftCards';
import { fmt } from '../../lib/mock-data';
import { toast } from '../../store/toast';
import {
  useParkedOrdersStore,
  buildSnapshotFromCart,
  summarizeForCard,
} from '../parked-orders';
import {
  ShoppingCartIcon, UserIcon, UserPlusIcon, TrashIcon,
  PlusIcon, MinusIcon, ArrowRightIcon, PauseIcon, PercentIcon,
  CreditCardIcon, FileTextIcon, SlidersIcon,
} from '../../components/icons';
import { useModifierItemsById } from '../catalog/hooks';
import { LineNoteModal } from './LineNoteModal';
import { CreatorButton } from './CreatorButton';
import { LineDiscountModal } from './LineDiscountModal';
import { avatarColor, getInitials } from '../../lib/mock-data';
import { useLang } from '../../lib/language';
import { nameFor, namePair } from '../../lib/localizedName';
import { usePromotionQuote } from './usePromotionQuote';
import { holdCart } from '../held-orders/holdCart';
import { MembershipDatesDialog } from '../membership/MembershipDatesDialog';
import { ensureManagerApproval, isHeldRemoval } from '../held-orders/heldEdit';
import { useHeldOrders } from '../../store/heldOrders';

/**
 * Cart panel — fixed 500px wide on the right of the workspace (desktop);
 * narrows on tablet and becomes a slide-in drawer on phones.
 *
 * Sections (top → bottom):
 *   - Order label (Walk-in / Delivery + order #)
 *   - Customer section (Walk-in customer placeholder OR selected customer)
 *   - Optional delivery info pill
 *   - Cart items list (or empty state)
 *   - Footer: summary + Charge button + Park/Discount/Discard
 *
 * As of Phase 6:
 *   - Park is real. It serializes the cart to a snapshot, fires
 *     POST /parked-orders in the background (optimistic; survives network
 *     blips via the parked-orders store's retry logic), and **resets the
 *     cart while keeping the cashier on the Walk-in tab** so they can
 *     start serving the next customer immediately.
 *   - The Parked Orders screen is reached via the sidebar's "Parked"
 *     icon or the topbar's "Open orders" tab — never auto-navigation
 *     after Park, which would interrupt the cashier's flow.
 */
export function CartPanel() {
  const { t: tr, lang } = useLang();
  const taxPercent = useTaxConfig((s) => s.config.sales_percentage);
  // Ask the server what its automatic promotions give this basket, so the
  // cashier can tell the customer before taking payment rather than after.
  usePromotionQuote();
  // True while the basket has changed and the server has not answered for it
  // yet — Charge waits, because the total on the button is not final.
  const promotionQuotePending = useCart((s) => s.promotionQuotePending);
  const taxLabel = useTaxConfig((s) => s.config.label_en);
  const lines = useCart((s) => s.lines);
  const customer = useCart((s) => s.customer);
  const table = useCart((s) => s.table);
  const delivery = useCart((s) => s.delivery);
  const discount = useCart((s) => s.discount);
  const loyaltyRedeem = useCart((s) => s.loyaltyRedeem);
  // The live ledger balance, when loyalty is on. Falls back to the customer
  // record's cached `LoyalityBalance` — which the ledger keeps in sync, but
  // which is only as fresh as the last customer fetch.
  const loyaltyStanding = useLoyalty((s) => s.customer);
  const pointsBalance =
    loyaltyStanding && loyaltyStanding.customer_id === customer?.id
      ? loyaltyStanding.balance_points
      : customer?.loyalty_balance ?? 0;
  const orderType = useCart((s) => s.orderType);
  const orderMode = useCart((s) => s.orderMode);
  const orderNumber = useCart((s) => s.orderNumber);
  const note = useCart((s) => s.note);
  const editingReceiptId = useCart((s) => s.editingReceiptId);
  const changeQty = useCart((s) => s.changeQty);
  const setQty = useCart((s) => s.setQty);
  const setLineTotal = useCart((s) => s.setLineTotal);
  const toggleLineReturn = useCart((s) => s.toggleLineReturn);
  const toggleLineRestock = useCart((s) => s.toggleLineRestock);
  // Separate / Transfer items of a HELD dine-in order (legacy permissions).
  const editingHeldOrder = useCart((s) => s.editingHeld && s.editingReceiptId != null);
  const canSplitOrTransfer = useCanI(POS_PERMISSIONS.SeparateTableOrders, POS_PERMISSIONS.TransferTableItems);
  const setLineNote = useCart((s) => s.setLineNote);
  const removeLine = useCart((s) => s.removeLine);
  const exchangeParentId = useCart((s) => s.exchangeParentId);
  const reset = useCart((s) => s.reset);

  // What is on the shelf for the lines in the cart. Re-asked (debounced) as
  // the cart changes; drives the per-line chip and, when the tenant blocks
  // negative stock, the refusal at Charge — in the cashier's language, before
  // the customer is at the card machine.
  const allowNegativeStock = useTenantConfig((s) => s.allowNegativeStock);
  const stockShortages = useStockLevels((s) => s.shortages);
  const openShortageDialog = useStockLevels((s) => s.openDialog);
  // While EDITING an invoice its own lines are already off the shelf and the
  // edit rewrites them, so the server is told which one to add back
  // (GL_WEB_kuwaitecunited #7652: an untouched ×17 on a shelf of 16 blocked
  // the edit). A held invoice moved no stock, and the server answers 0 for it.
  useEffect(() => {
    refreshStockLevels(lines, 350, editingReceiptId);
  }, [lines, editingReceiptId]);
  // Summed per (item, UNIT): the chip compares this against a balance expressed
  // in that same unit, so adding a carton line's 2 to a piece line's 3 would
  // compare 5 of nothing against one of them. Lines of the same item in
  // different units each get their own chip, which is also what the cashier
  // needs — they are two different things on the shelf.
  const soldQtyOf = (itemId: number, unitId: number | null): number =>
    lines.reduce(
      (s, l) =>
        !l.isReturn && l.product.id === itemId && (l.product.unitId ?? null) === unitId
          ? s + l.qty
          : s,
      0,
    );

  // Admin flag (mstr_ConfigurationParameter → IsTotalPriceChanges). When on, a
  // line's total is tap-to-edit; WhenChangeTotal decides what the edit adjusts.
  const canEditTotal = useTenantConfig((s) => s.isTotalPriceChanges);
  const whenChangeTotal = useTenantConfig((s) => s.whenChangeTotal);

  // The THIRD gate on a line's price: Security_Users.ChangeSalesPriceMode,
  // the admin Users form's "Change selling price". Never configured (0) is
  // allowed — see priceChangeRule.ts.
  const priceRule = usePriceChangeRule();

  /**
   * Commit an edited line total, refusing a move the cashier's
   * ChangeSalesPriceMode does not permit.
   *
   * Only the PRICE mode is checked: with `whenChangeTotal === 'quantity'`
   * an edited total back-solves the quantity and the unit price never
   * moves, so the mode has nothing to say about it.
   *
   * The comparison is against the line's CATALOG price (its base plus its
   * modifier deltas — the same sum `repriceLinesForTier` rebuilds), not
   * against its current unitPrice. Comparing against the current one would
   * let "increase only" ratchet a price down one edit at a time, since each
   * new edit would only have to beat the previous discount.
   */
  const unitPriceMoveAllowed = (line: CartLine, nextUnit: number): boolean => {
    const catalogUnit =
      line.product.price + line.mods.reduce((sum, m) => sum + m.price, 0);
    if (isPriceChangeAllowed(priceRule, catalogUnit, nextUnit)) return true;
    toast(
      priceRule === 'increase-only'
        ? tr({
            en: 'Your account may only increase the selling price.',
            ar: 'صلاحيتك تسمح بزيادة سعر البيع فقط.',
          })
        : tr({
            en: 'Your account may not change the selling price.',
            ar: 'صلاحيتك لا تسمح بتغيير سعر البيع.',
          }),
      'error',
    );
    return false;
  };
  const commitLineTotal = (line: CartLine, newTotal: number): void => {
    if (whenChangeTotal === 'price' && line.qty > 0 && !unitPriceMoveAllowed(line, newTotal / line.qty)) return;
    setLineTotal(line.cartId, newTotal, whenChangeTotal);
  };
  /**
   * Commit an edited UNIT price — the legacy item dialog's price box. Gated on
   * the `Edit Cart Item Price` permission and ChangeSalesPriceMode only; unlike
   * the total, the tenant's IsTotalPriceChanges flag is not involved, exactly
   * as legacy showed that box to anyone holding the permission.
   */
  const setLineUnitPrice = useCart((s) => s.setLineUnitPrice);
  const commitLineUnitPrice = (line: CartLine, nextUnit: number): void => {
    if (!unitPriceMoveAllowed(line, nextUnit)) return;
    setLineUnitPrice(line.cartId, nextUnit);
  };
  // Membership feature flag — gates the "View Membership" (redeem) button.
  const isMembershipEnabled = useTenantConfig((s) => s.isMembership);
  // Restaurant flag — non-restaurant tenants have no tables at all, so the
  // table chip below would be a dead end (the Dine-in pill is already hidden).
  const isRestaurant = useTenantConfig((s) => s.isRestaurant);
  // POS_CustomerMeasurements: a "Measurements" button on the customer card.
  const measurementsOn = useTenantConfig((s) => s.customerMeasurements);
  const [measuresOpen, setMeasuresOpen] = useState(false);
  // Gift cards — the button only appears when the tenant has the feature on AND
  // has nominated the catalog item a card sale is rung on. Without that item a
  // sale has nothing to write to Inv_Receipt_d.
  const canSellGiftCards = useGiftCards((s) => s.canSell)();
  // Per-tenant flag (mstr_ConfigurationParameter → IsSalesManMandatory). When on,
  // every sold (non-membership) line must carry Salesman 1 before charging.
  const isSalesmanMandatory = useTenantConfig((s) => s.isSalesmanMandatory);
  // IsSalesManImplemented — the master gate. Legacy skips loading salesmen
  // altogether when it is off (OrderController.cs:4396), so we do the same:
  // no flag, no request.
  const isSalesmanImplemented = useTenantConfig((s) => s.isSalesmanImplemented);
  // ActivateSecondSalesman — the optional second slot per line.
  const activateSecondSalesman = useTenantConfig((s) => s.activateSecondSalesman);
  const setLineSalesman = useCart((s) => s.setLineSalesman);

  // Salesman-role users for the per-line dropdowns. Loaded once (5-min cache);
  // an empty list simply hides the dropdowns (tenant has no Salesman users).
  const { data: salesmen = [] } = useQuery({
    queryKey: ['salesmen'],
    queryFn: () => salesmenAPI.list(),
    staleTime: 5 * 60 * 1000,
    enabled: isSalesmanImplemented,
  });

  // A sold line is "missing" its required salesman when the tenant mandates it
  // and the line has no Salesman 1 (membership + return lines are exempt).
  // Mandatory only counts while the feature is on — otherwise a tenant with
  // IsSalesManMandatory left true but IsSalesManImplemented off could never
  // charge anything, with no visible control to fix it.
  const salesmanMissing =
    isSalesmanImplemented &&
    isSalesmanMandatory &&
    salesmen.length > 0 &&
    lines.some((l) => !l.membershipDetailId && !l.isReturn && l.salesman1Id == null);

  // Per-tenant flag (mstr_ConfigurationParameter → CustomerIsMandatory). The
  // server enforces this too; checking here means Charge opens the customer
  // picker instead of failing a round trip — deliberately NOT disabling the
  // button, because the press is what takes the cashier to the fix. Delivery
  // is exempt: its own modal collects the customer before it will submit.
  const isCustomerMandatory = useTenantConfig((s) => s.isCustomerMandatory);
  const customerMissing =
    isCustomerMandatory && orderMode !== 'delivery' && customer == null;

  // Per-user POS permissions (legacy parity). Holding the permission shows the
  // action; lacking it hides the control — like UserHelper.IsAuthorized.
  //
  // Note: taking a line back OUT of the cart, and emptying the cart, are NOT
  // gated. Nothing has been posted yet — the cart is a draft, and both actions
  // only undo a mis-scan. Cancel Item / Cancel Order stay for the operations
  // that touch a SAVED receipt (edit / void), which is what they mean in the
  // legacy POS.
  const canEditCartItemPrice = useCanI(POS_PERMISSIONS.EditCartItemPrice);

  // The cart line whose note is being edited, if any. Local rather than in the
  // `ui` modal store because the target is a cart line, not a catalog item —
  // the store's modal payload has nowhere to put it.
  const [noteTargetId, setNoteTargetId] = useState<string | null>(null);
  const [discountTargetId, setDiscountTargetId] = useState<string | null>(null);
  const noteTarget = lines.find((l) => l.cartId === noteTargetId) ?? null;
  const discountTarget = lines.find((l) => l.cartId === discountTargetId) ?? null;
  // Security_Users.ShowReceiptItemDiscount — the "Allow item discount" tick on
  // the admin Users form. Absent on a token minted before the field existed,
  // which reads as "not allowed": the server enforces the same rule, so
  // showing the control to a cashier who cannot use it would only produce a
  // rejected sale at the moment of payment.
  const canDiscountLine = useAuth().cashier?.permissions?.allow_item_discount === true;
  const setLineDiscount = useCart((s) => s.setLineDiscount);

  const showModal = useUI((s) => s.showModal);
  // Items with option groups, so a line can reopen its options window.
  const modifierItems = useModifierItemsById();
  // The item code is copyable, as on the legacy till: tap it to copy.
  const copyItemCode = (code: string): void => {
    const done = () => toast(tr({ en: `Copied ${code}`, ar: `تم نسخ ${code}` }), 'success');
    try {
      if (navigator.clipboard?.writeText) {
        void navigator.clipboard.writeText(code).then(done, () => toast(code, 'info'));
        return;
      }
    } catch {
      /* fall through */
    }
    toast(code, 'info');
  };
  const cartOpen = useUI((s) => s.cartOpen);
  const setCartOpen = useUI((s) => s.setCartOpen);
  const toggleCart = useUI((s) => s.toggleCart);

  const park = useParkedOrdersStore((s) => s.park);
  const cashierBranchId = useAuth().cashier?.branch_id ?? null;
  const [holding, setHolding] = useState(false);
  const editingHeld = useCart((s) => s.editingHeld);
  // IsHoldImplemented — when off, parking is unavailable to this tenant.
  const isHoldImplemented = useTenantConfig((s) => s.isHoldImplemented);
  // LAUNDRY: a new order is TAKEN IN (held, paid at pickup) with a ready-by
  // date, and its ticket + bag tag print with the order barcode.
  const laundryOn = useModuleOn('laundry');
  const [readyByOpen, setReadyByOpen] = useState(false);
  const [membershipDatesOpen, setMembershipDatesOpen] = useState(false);
  const printLaundryTicket = useLaundryPrint();

  const t = useCartTotals();
  // Total units in the cart (sum of line quantities) — drives the badge on
  // the mobile "view order" bar.
  const itemCount = lines.reduce((n, l) => n + l.qty, 0);
  const orderTypeLabel =
    orderMode === 'delivery'
      ? tr({ en: 'Delivery order', ar: 'طلب توصيل' })
      : orderMode === 'dine-in'
        ? tr({ en: 'Dine-in order', ar: 'طلب صالة' })
        : orderMode === 'pickup'
          ? tr({ en: 'Pickup order', ar: 'طلب استلام' })
          : orderMode === 'membership'
            ? tr({ en: 'Membership order', ar: 'طلب عضوية' })
            : tr({ en: 'Walk-In order', ar: 'طلب مباشر' });
  // Dine-in requires a table before the sale can be charged.
  const needsTable = orderMode === 'dine-in' && !table;

  // The return lines the tenant wants a restock decision on — the same set the
  // legacy's PreCancelOrder() builds, and the same set that already shows the
  // inline checkbox on the cart line.
  const restockLines = lines.filter((l) => l.isReturn && l.product.restockOnReturn);
  // Asked once per basket. The signature changes whenever the set of lines
  // under the decision changes, so adding another returned item re-asks rather
  // than carrying a stale confirmation into a different cart.
  const restockKey = restockLines.map((l) => l.cartId).join('|');
  const [restockAskedFor, setRestockAskedFor] = useState<string | null>(null);
  const [restockOpen, setRestockOpen] = useState(false);

  function openCharge() {
    if (lines.length === 0) return;
    // Dine-in must have a table attached before charging. The Charge button is
    // already disabled in this state; this guards the keyboard/programmatic path.
    if (needsTable) {
      toast(tr({ en: 'Attach a table before charging a dine-in order', ar: 'أرفق طاولة قبل تحصيل طلب الصالة' }), 'warning');
      showModal('tablePicker');
      return;
    }
    // Salesman mandatory (per-tenant): every sold line needs a Salesman 1.
    if (salesmanMissing) {
      toast(tr({ en: 'Select a salesman for every item before charging', ar: 'اختر مندوبًا لكل صنف قبل التحصيل' }), 'warning');
      return;
    }
    // Negative stock blocked (per-tenant, mstr_InventroySettings): the server
    // will refuse this basket, so say which items and by how much now. The
    // server re-checks under a lock either way — this is the friendly copy.
    if (!allowNegativeStock && stockShortages.length > 0) {
      openShortageDialog(stockShortages, true);
      return;
    }
    // Customer mandatory (per-tenant): open the picker rather than just
    // refusing, so the cashier lands where the problem is fixed.
    if (customerMissing) {
      toast(tr({ en: 'Select a customer before charging', ar: 'اختر عميلاً قبل التحصيل' }), 'warning');
      showModal('customerPicker');
      return;
    }
    // Last gate before the money: does the returned stock go back on the shelf?
    // The legacy asks here too — at confirm time, not while the cart is being
    // built — because that is the moment the goods are physically in hand.
    if (restockLines.length > 0 && restockAskedFor !== restockKey) {
      setRestockOpen(true);
      return;
    }
    // Membership (subscription) invoice: a cash customer owns the membership,
    // and its From / To dates are asked before the payment window, as legacy.
    if (orderMode === 'membership' && editingReceiptId == null) {
      if (!customer || customer.type !== 'cash') {
        toast(tr({ en: 'Select a cash customer for a membership invoice', ar: 'اختر عميلاً نقدياً لفاتورة الاشتراك' }), 'warning');
        showModal('customerPicker');
        return;
      }
      // The cart drawer stays open under the dialog (it renders inside it on
      // mobile); it is slid away on confirm, before the payment window.
      setMembershipDatesOpen(true);
      return;
    }
    // On mobile the cart is a drawer over the catalog — slide it away so the
    // payment/delivery modal isn't stacked behind it.
    setCartOpen(false);
    // Delivery mode → DeliveryConfirmModal (Phase 9.6); other modes → regular
    // payment flow. The delivery modal handles its own submit + dispatch.
    if (orderMode === 'delivery') {
      showModal('deliveryConfirm');
      return;
    }
    showModal('payment');
  }

  /**
   * HOLD the current cart — a real invoice with IsHolded = 1 on the Unpaid
   * tender, like the legacy till's Hold (see features/held-orders/holdCart).
   * It goes to the kitchen now and is printed, paid or cancelled from the
   * Held Orders screen. A cart the held invoice cannot represent (delivery,
   * membership, a return) or an offline till falls back to the local hold
   * below, so nothing the cashier typed is lost.
   */
  /**
   * Lower or remove a line — but on a HELD order reopened for editing, taking
   * away something it already had needs the manager's password (asked once
   * per edit; the server checks it again when the order is saved).
   */
  async function guardedQty(line: CartLine, newQty: number, apply: () => void) {
    if (isHeldRemoval(line, newQty) && !(await ensureManagerApproval())) return;
    apply();
  }

  async function takeInLaundry(readyBy: string) {
    if (lines.length === 0 || holding || cashierBranchId == null) return;
    setHolding(true);
    try {
      const outcome = await holdCart(cashierBranchId, { readyBy });
      if (outcome.kind === 'held') {
        setReadyByOpen(false);
        toast(`${tr({ en: 'Taken in', ar: 'تم الاستلام' })} · #${outcome.entryNo} · ${fmt(outcome.total)}`, 'success');
        void printLaundryTicket(outcome.receiptId);
        reset();
        setCartOpen(false);
        void useHeldOrders.getState().refresh();
        return;
      }
      // A laundry order is never parked locally: it must exist as an invoice
      // to carry its number and barcode.
      toast(
        outcome.kind === 'refused'
          ? typeof outcome.message === 'string' ? outcome.message : tr(outcome.message)
          : tr({
              en: 'This order cannot be taken in right now (offline, or a return / membership in the cart).',
              ar: 'لا يمكن استلام هذا الطلب الآن (بدون اتصال، أو يوجد مرتجع / عضوية في السلة).',
            }),
        'error',
      );
    } finally {
      setHolding(false);
    }
  }

  async function holdOrder() {
    if (lines.length === 0) {
      toast(tr({ en: 'Nothing to hold', ar: 'لا يوجد ما يمكن تعليقه' }), 'warning');
      return;
    }
    if (holding || cashierBranchId == null) return;
    setHolding(true);
    try {
      const outcome = await holdCart(cashierBranchId);
      if (outcome.kind === 'held') {
        toast(
          `${outcome.updated ? tr({ en: 'Hold updated', ar: 'تم تحديث الطلب المعلق' }) : tr({ en: 'Held', ar: 'مُعلّق' })} · #${outcome.entryNo} · ${fmt(outcome.total)}`,
          'success',
        );
        reset();
        setCartOpen(false);
        void useHeldOrders.getState().refresh();
        return;
      }
      if (outcome.kind === 'refused') {
        toast(typeof outcome.message === 'string' ? outcome.message : tr(outcome.message), 'error');
        return;
      }
      await parkOrder();
    } finally {
      setHolding(false);
    }
  }

  /**
   * The LOCAL hold — the fallback for a cart the held invoice cannot carry.
   *
   * Hybrid local-first: the parked-orders store inserts a row optimistically
   * so the card grid updates instantly, then fires POST /parked-orders in
   * the background. If the POST fails the row stays visible (red dot) and
   * is retried on the next refresh — recall still works while pending or
   * failed because the snapshot lives in localStorage.
   *
   * After park, the cart resets but the cashier stays on Walk-in. The
   * counter in the sidebar (and topbar) updates live so they can see the
   * order landed.
   */
  async function parkOrder() {
    if (lines.length === 0) {
      toast(tr({ en: 'Nothing to hold', ar: 'لا يوجد ما يمكن تعليقه' }), 'warning');
      return;
    }

    const snapshot = buildSnapshotFromCart({
      lines: lines.map((l) => ({
        product: {
          id: l.product.id,
          code: String(l.product.id),
          name: l.product.name,
          nameAr: l.product.nameAr,
          unitId: l.product.unitId ?? null,
          unitName: l.product.unitName ?? null,
          unitNameAr: l.product.unitNameAr ?? null,
        },
        qty: l.qty,
        unitPrice: l.unitPrice,
        note: l.note ?? null,
        mods: l.mods,
      })),
      customer: customer
        ? {
            id: customer.id,
            type: customer.type,
            name: customer.name,
            name_ar: customer.name_ar,
            phone: customer.phone,
          }
        : null,
      delivery: delivery
        ? { fee: delivery.fee, zone: delivery.zone, address: delivery.address }
        : null,
      discount: discount
        ? { type: discount.type, value: discount.value }
        : null,
      // Capture pickup so a parked pickup order recalls onto the Pickup pill;
      // every other mode parks under its existing walkin/delivery tag.
      orderType: orderMode === 'pickup' ? 'pickup' : orderType,
      note,
    });

    const summary = summarizeForCard(snapshot);

    // Optimistic insert + background POST. Returns immediately.
    await park({
      snapshot,
      note,
      customerLabel: summary.customerLabel,
      itemCount: summary.itemCount,
      totalValue: summary.totalValue,
    });

    // Toast that confirms the save AND tells the cashier where to find it
    // — friendlier than a tab jump for the high-throughput case where
    // they're parking one ticket and immediately taking another.
    toast(
      `${tr({ en: 'Held', ar: 'مُعلّق' })} · ${summary.itemCount} ${
        summary.itemCount === 1 ? tr({ en: 'item', ar: 'صنف' }) : tr({ en: 'items', ar: 'أصناف' })
      } · ${fmt(summary.totalValue)}`,
      'success',
    );

    reset();
    setCartOpen(false);
    // No tab change — keep the cashier in Walk-in ready for the next sale.
  }

  /** Empty the cart — for items added by mistake. Nothing is posted yet. */
  function clearCart() {
    if (lines.length === 0) return;
    if (confirm(tr({
      en: 'Clear the cart? All items will be removed.',
      ar: 'تفريغ السلة؟ سيتم حذف جميع الأصناف.',
    }))) {
      reset();
      setCartOpen(false);
      toast(tr({ en: 'Cart cleared', ar: 'تم تفريغ السلة' }), 'info');
    }
  }

  function requestDiscount() {
    // Phase 8b: opens the real Discount modal (was 'override' stub).
    showModal('discount');
  }

  return (
    <>
      {/* Mobile-only backdrop behind the cart drawer. Tapping it slides the
          cart away. Hidden (display:none) on tablet/desktop where the cart
          is a permanent column. */}
      <div
        className={`cart-backdrop ${cartOpen ? 'show' : ''}`}
        onClick={() => setCartOpen(false)}
        aria-hidden="true"
      />

      <aside className={`cart-panel ${cartOpen ? 'open' : ''}`}>
        {/* Mobile-only drawer close handle (top-right). */}
        <button
          type="button"
          className="cart-drawer-close"
          onClick={() => setCartOpen(false)}
          title={tr({ en: 'Close cart', ar: 'إغلاق السلة' })}
          aria-label={tr({ en: 'Close cart', ar: 'إغلاق السلة' })}
        >
          ✕
        </button>

      {/* Header */}
      <div className="cart-header">
        {/* Order type, customer and table side by side in ONE row (they were
            three stacked rows): each is a compact tile that opens its own
            picker, and the height they gave back goes to the item list.
            Wraps onto two rows only on a phone too narrow for three. */}
        <div className="cart-header-row">
        <div className="cart-order-label">
          <div className="cart-order-icon">
            <UserIcon size={16} />
          </div>
          <div className="cart-order-text">
            <div className="cart-order-type">{orderTypeLabel}</div>
            <div className="cart-order-sub">
              <span className="cart-order-no">#{orderNumber}</span>
              {/* Change Salesman — who this receipt is credited to. */}
              <CreatorButton />
            </div>
          </div>
        </div>

        <div
          className={`customer-section ${customer ? 'has-customer' : ''}`}
          onClick={() => {
            if (customer) {
              useCustomerPicker.getState().setSourceType(customer.type);
              useCustomerPicker.getState().selectId(customer.id);
            }
            showModal('customerPicker');
          }}
        >
          {customer ? (
            <div
              className="customer-avatar"
              style={{ background: avatarColor(customer.id) }}
            >
              {getInitials(customer.name)}
            </div>
          ) : (
            <div className="customer-empty">
              <UserPlusIcon size={18} />
            </div>
          )}
          <div className="customer-info">
            <div className="customer-name">
              {customer ? (customer.name ?? `#${customer.code ?? customer.id}`) : tr({ en: 'Walk-in customer', ar: 'عميل مباشر' })}
            </div>
            <div className="customer-meta">
              {customer
                ? `${customer.phone ?? '—'} · ${pointsBalance.toLocaleString()} ${tr({ en: 'pts', ar: 'نقطة' })}`
                : tr({ en: 'Tap to add a customer · earn loyalty', ar: 'اضغط لإضافة عميل · اكسب نقاط الولاء' })}
            </div>
          </div>
          {/* Orders — the customer's previous receipts, each printable. Its
              own click, so it does not open the customer picker behind it. */}
          {customer && (
            <button
              type="button"
              className="btn-secondary customer-orders-btn"
              onClick={(e) => {
                e.stopPropagation();
                showModal('customerOrders');
              }}
            >
              {tr({ en: 'Orders', ar: 'الطلبات' })}
            </button>
          )}
          {customer && measurementsOn && (
            <button
              type="button"
              className="btn-secondary customer-orders-btn"
              onClick={(e) => {
                e.stopPropagation();
                setMeasuresOpen(true);
              }}
            >
              {tr({ en: 'Measurements', ar: 'المقاسات' })}
            </button>
          )}
        </div>
        {measuresOpen && customer && (
          <CustomerMeasurementsModal
            customerType={customer.type}
            customerId={customer.id}
            customerName={customer.name ?? null}
            onClose={() => setMeasuresOpen(false)}
          />
        )}

        {/* Table section — mirrors the customer section above. The picker
            attaches a BranchTables row to the cart; on Charge the receipt
            stores BranchTableId so the kitchen ticket and customer receipt
            print the table label. Shown only when the tenant is a restaurant
            (Settings → POS Settings → IsRestaurant). */}
        {isRestaurant && (
          <div
            className={`customer-section ${table ? 'has-customer' : ''}`}
            onClick={() => showModal('tablePicker')}
            style={needsTable ? { borderColor: 'var(--danger, #dc2626)' } : undefined}
          >
            <div
              className={table ? 'customer-avatar' : 'customer-empty'}
              style={table ? { background: 'var(--primary, #4f46e5)', color: '#fff' } : undefined}
            >
              {table ? 'T' : <UserPlusIcon size={18} />}
            </div>
            <div className="customer-info">
              <div className="customer-name">
                {table ? table.label : needsTable ? tr({ en: 'Table required', ar: 'الطاولة مطلوبة' }) : tr({ en: 'No table', ar: 'لا توجد طاولة' })}
              </div>
              <div
                className="customer-meta"
                style={needsTable ? { color: 'var(--danger, #dc2626)' } : undefined}
              >
                {table
                  ? tr({ en: 'Tap to change · tap Detach in the picker to clear', ar: 'اضغط للتغيير · اضغط فصل في القائمة للإزالة' })
                  : needsTable
                    ? tr({ en: 'Dine-in orders need a table before charging', ar: 'طلبات الصالة تحتاج طاولة قبل التحصيل' })
                    : tr({ en: 'Tap to attach a table', ar: 'اضغط لإرفاق طاولة' })}
              </div>
            </div>
          </div>
        )}
        </div>

        {/* View Membership — opens the redeem modal for the selected cash
            customer's prepaid packages. Shown only when the tenant has the
            Membership feature on (Settings → POS Settings). */}
        {isMembershipEnabled && (
          <button
            type="button"
            className="btn-secondary"
            style={{ marginTop: 8, width: '100%' }}
            onClick={() => {
              if (!customer || customer.type !== 'cash') {
                toast(tr({ en: 'Select a cash customer to view memberships', ar: 'اختر عميلاً نقدياً لعرض العضويات' }), 'info');
                return;
              }
              showModal('membership');
            }}
          >
            <CreditCardIcon size={16} />
            {tr({ en: 'View Membership', ar: 'عرض العضوية' })}
          </button>
        )}

        {/* Sell a gift card — adds one card-sale line to the cart at face
            value. The card itself is created by the order transaction when the
            sale is charged. Shown only when the tenant has gift cards on AND has
            nominated the catalog item a card sale is rung on. */}
        {canSellGiftCards && (
          <button
            type="button"
            className="btn-secondary"
            style={{ marginTop: 8, width: '100%' }}
            onClick={() => showModal('sellGiftCard')}
          >
            <span aria-hidden="true">🎁</span>
            {tr({ en: 'Sell gift card', ar: 'بيع بطاقة هدايا' })}
          </button>
        )}

        {/* How tables work — the manual's Dine-in tables chapter, in a pop-up
            in the till (no admin app, no sign-in). Shown once a dine-in order
            has a table. */}
        {isRestaurant && table && orderMode === 'dine-in' && (
          <button
            type="button"
            onClick={() => showModal('tablesManual')}
            style={{ display: 'block', marginTop: 6, padding: 0, border: 0, background: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--primary, #4f46e5)', textDecoration: 'underline' }}
          >
            {tr({ en: 'How tables work (split, transfer, merge, waiter)', ar: 'كيف تعمل الطاولات (فصل، نقل، دمج، نادل)' })}
          </button>
        )}

        {isRestaurant && table && editingHeldOrder && canSplitOrTransfer && (
          <button
            type="button"
            className="btn-secondary"
            style={{ marginTop: 8, width: '100%' }}
            onClick={() => {
              if (requireSavedHeld()) showModal('splitTransfer');
            }}
          >
            <span aria-hidden="true">⇄</span>{' '}
            {tr({ en: 'Separate / Transfer items', ar: 'فصل / نقل الأصناف' })}
          </button>
        )}

        {delivery && (
          <div className="delivery-info show">
            <div className="delivery-row">
              <span className="label">{tr({ en: 'Zone', ar: 'المنطقة' })}</span>
              <span className="value">{delivery.zone}</span>
            </div>
            <div className="delivery-row">
              <span className="label">{tr({ en: 'ETA', ar: 'الوقت المتوقع' })}</span>
              <span className="value">{delivery.eta} {tr({ en: 'min', ar: 'دقيقة' })}</span>
            </div>
            <div className="delivery-row">
              <span className="label">{tr({ en: 'Fee', ar: 'الرسوم' })}</span>
              <span className="value">{fmt(delivery.fee)}</span>
            </div>
          </div>
        )}
      </div>

      {editingReceiptId != null && (
        <div className="cart-edit-banner">
          <span>{tr({ en: 'Editing order', ar: 'تعديل الطلب' })} #{editingReceiptId}</span>
          <button
            className="cart-edit-cancel"
            onClick={() => {
              if (confirm(tr({ en: 'Cancel editing and discard changes?', ar: 'إلغاء التعديل وتجاهل التغييرات؟' }))) {
                reset();
                toast(tr({ en: 'Edit cancelled', ar: 'تم إلغاء التعديل' }), 'info');
              }
            }}
          >
            {tr({ en: 'Cancel Edit', ar: 'إلغاء التعديل' })}
          </button>
        </div>
      )}

      {orderMode === 'delivery' && (
        <div className="cart-delivery-banner">
          {tr({
            en: 'Delivery order — customer & driver required at checkout.',
            ar: 'طلب توصيل — العميل والسائق مطلوبان عند الدفع.',
          })}
        </div>
      )}

      {/* Items list */}
      <div className="cart-items">
        {lines.length === 0 ? (
          <div className="cart-empty">
            <div className="cart-empty-icon">
              <ShoppingCartIcon size={36} />
            </div>
            <div className="cart-empty-text">{tr({ en: 'No items yet', ar: 'لا توجد أصناف بعد' })}</div>
            <div className="cart-empty-hint">{tr({ en: 'Tap a product to add to the order', ar: 'اضغط على منتج لإضافته إلى الطلب' })}</div>
          </div>
        ) : (
          <>
          {/* Column headings for the Item | Price | Qty | Discount | Total table below. */}
          <div className="cart-cols-head" aria-hidden="true">
            <span className="ci-item">{tr({ en: 'Item', ar: 'الصنف' })}</span>
            <span className="ci-price">{tr({ en: 'Price', ar: 'السعر' })}</span>
            <span className="ci-qty">{tr({ en: 'Qty', ar: 'الكمية' })}</span>
            <span className="ci-disc">{tr({ en: 'Discount', ar: 'الخصم' })}</span>
            <span className="ci-total">{tr({ en: 'Total', ar: 'الإجمالي' })}</span>
            <span className="ci-remove" />
          </div>
          {lines.map((line) => {
            const sign = line.isReturn ? -1 : 1;
            // The price stays as sold, on a return line too (the user,
            // 2026-09-28): discounts show as discounts, never as a changed price.
            // The line's own discount — shown read-only beside its price on
            // every line (it is edited from the % button's modal). On a return
            // it also carries the discount inherited from the original sale.
            const ownDisc = lineDiscountAmount(line, (n) => roundMoney(n, moneyPrecision()));
            // An item-level promotion is booked on the line (2026-10-07), so it
            // shows in the line's Discount and Total — not as a row below.
            const linePromo = line.isReturn ? 0 : (t.promotionByLine[line.cartId] ?? 0);
            const lineDisc = roundMoney(ownDisc + linePromo, moneyPrecision());
            // What the line is worth after its own discount — the Total column,
            // so the column adds up to the footer's Subtotal (2026-10-01).
            const lineNet = sign * (line.unitPrice * line.qty - lineDisc);
            const discountable = canDiscountLine && !line.membershipDetailId && !line.giftCardIssue;
            const modAdd = line.mods.filter((m) => m.price > 0);
            const modZero = line.mods.filter((m) => m.price === 0 && !m.name.startsWith('No '));
            const modRem = line.mods.filter((m) => m.name.startsWith('No '));
            // Same rule as the modifier popup the cashier just came from: one
            // name, in the active language. `name` stays the classifier above
            // — the "No " prefix is written in English regardless of script.
            const modName = (m: { name: string; nameAr: string; qty?: number }): string => {
              const n = nameFor(lang, { name: m.name, name_ar: m.nameAr });
              // "Fabric × 3.5" for a "choices with quantity" option.
              return m.qty != null && m.qty !== 1 ? `${n} × ${m.qty}` : n;
            };
            // The unit price may be typed over by a cashier who may change
            // prices (see commitLineUnitPrice); everyone else sees it read-only.
            const unitPriceEditable =
              canEditCartItemPrice &&
              priceRule !== 'none' &&
              !line.membershipDetailId &&
              !line.giftCardIssue &&
              !line.isReturn;
            return (
              // One row of the Item | Price | Qty | Total table (2026-09-30
              // redesign). The cells line up under `.cart-cols-head`; the
              // restock and salesman controls span the full row beneath.
              <div className={`cart-item${line.isReturn ? ' cart-item--return' : ''}`} key={line.cartId}>
                <div className="ci-item">
                  <div className="cart-item-name">
                    {line.isReturn && (
                      <span className="cart-item-return-tag">{tr({ en: 'RETURN', ar: 'مرتجع' })}</span>
                    )}
                    <span dir="auto">
                      {namePair(lang, { name: line.product.name, name_ar: line.product.nameAr }).primary}
                    </span>
                    {(line.product.unitName || line.product.unitNameAr) && (
                      <span className="cart-item-unit">
                        {(lang === 'ar' ? line.product.unitNameAr : line.product.unitName) ||
                          line.product.unitName ||
                          line.product.unitNameAr}
                      </span>
                    )}
                  </div>
                  {(() => {
                    const alt = namePair(lang, { name: line.product.name, name_ar: line.product.nameAr }).secondary;
                    return alt ? <div className="cart-item-name-alt" dir="auto">{alt}</div> : null;
                  })()}
                  {(modZero.length > 0 || modAdd.length > 0 || modRem.length > 0 || line.note) && (
                    <div className="cart-item-mods">
                      {/* One line per chosen option — a long list (fabrics, add-ons)
                          joined with " · " wrapped into an unreadable paragraph. */}
                      {modZero.map((m, i) => <div key={`z${m.option_id}-${i}`}>{modName(m)}</div>)}
                      {modAdd.map((m, i) => (
                        <div key={`a${m.option_id}-${i}`} className="add">
                          {`+ ${modName(m)} (${fmt(m.price)})`}
                        </div>
                      ))}
                      {modRem.map((m, i) => (
                        <div key={`r${m.option_id}-${i}`} className="rem">{modName(m)}</div>
                      ))}
                      {line.note && (
                        <button
                          type="button"
                          className="cart-item-note"
                          onClick={() => setNoteTargetId(line.cartId)}
                          title={tr({ en: 'Edit note', ar: 'تعديل الملاحظة' })}
                        >
                          📝 {line.note}
                        </button>
                      )}
                    </div>
                  )}
                  {/* The line's item-level offer — its money is already in the
                      Discount box; this only says WHY, beside the stock pill
                      where there is room for the word (the box has none). */}
                  {linePromo > 0 && (
                    <span className="offer-chip">
                      {tr({ en: 'Offer', ar: 'عرض' })} −{fmt(linePromo)}
                    </span>
                  )}
                  {!line.giftCardIssue && !line.membershipDetailId && (
                    <StockChip
                      itemId={line.product.id}
                      unitId={line.product.unitId ?? null}
                      requested={soldQtyOf(line.product.id, line.product.unitId ?? null)}
                      isReturn={line.isReturn}
                    />
                  )}
                  {/* The item code (tap to copy) and the line's small actions,
                      on ONE row under the name so the line stays short. */}
                  <div className="ci-sub">
                  {line.product.code && (
                    <button
                      type="button"
                      className="ci-code"
                      onClick={() => copyItemCode(line.product.code!)}
                      title={tr({ en: 'Copy code', ar: 'نسخ الكود' })}
                    >
                      {tr({ en: 'Code', ar: 'كود' })} {line.product.code}
                    </button>
                  )}
                  <div className="ci-actions">
                    {/* Change this line's options (modifiers) — the options
                        window reopens on the line's own choices and updates
                        it in place. On a new sale and on an edited receipt. */}
                    {(() => {
                      const catalogItem = modifierItems.get(line.product.id);
                      if (!catalogItem || line.isReturn || line.membershipDetailId || line.giftCardIssue) return null;
                      return (
                        <button
                          className={`cart-item-note-btn${line.mods.length > 0 ? ' has-note' : ''}`}
                          onClick={() =>
                            showModal('modifier', {
                              product: catalogItem,
                              meta: {
                                station: line.product.station,
                                emoji: line.product.emoji,
                                bg: line.product.bg,
                                image: line.product.image ?? null,
                                editCartId: line.cartId,
                              },
                            })
                          }
                          title={tr({ en: 'Change options', ar: 'تعديل الإضافات' })}
                          aria-label={tr({ en: 'Change options', ar: 'تعديل الإضافات' })}
                        >
                          <SlidersIcon size={14} />
                        </button>
                      );
                    })()}
                    {/* Flip between sold and returned (direct exchange). Hidden
                        for prepaid membership lines. */}
                    {!line.membershipDetailId && exchangeParentId == null && (
                      <button
                        className={`cart-item-return-toggle${line.isReturn ? ' is-return' : ''}`}
                        onClick={() => toggleLineReturn(line.cartId)}
                        title={line.isReturn ? tr({ en: 'Mark as sold', ar: 'تحديد كمبيع' }) : tr({ en: 'Mark as return', ar: 'تحديد كمرتجع' })}
                      >
                        ⇄
                      </button>
                    )}
                    {/* "No onions" — the same per-item note a QR-menu
                        customer types for themselves. Offered on every line
                        (a gift card or a prepaid membership line reaches no
                        kitchen, so those are left out). */}
                    {!line.membershipDetailId && !line.giftCardIssue && (
                      <button
                        className={`cart-item-note-btn${line.note ? ' has-note' : ''}`}
                        onClick={() => setNoteTargetId(line.cartId)}
                        title={
                          line.note
                            ? tr({ en: 'Edit note', ar: 'تعديل الملاحظة' })
                            : tr({ en: 'Add note', ar: 'إضافة ملاحظة' })
                        }
                        aria-label={
                          line.note
                            ? tr({ en: 'Edit note', ar: 'تعديل الملاحظة' })
                            : tr({ en: 'Add note', ar: 'إضافة ملاحظة' })
                        }
                      >
                        <FileTextIcon size={14} />
                      </button>
                    )}
                  </div>
                  </div>
                </div>

                {/* Price — the unit price as sold. */}
                <div className="ci-price" data-label={tr({ en: 'Price', ar: 'السعر' })}>
                  {unitPriceEditable ? (
                    <EditableUnitPrice
                      value={line.unitPrice}
                      onCommit={(n) => commitLineUnitPrice(line, n)}
                    />
                  ) : (
                    <div className="ci-box">{fmt(line.unitPrice)}</div>
                  )}
                </div>

                {/* Qty */}
                <div className="ci-qty" data-label={tr({ en: 'Qty', ar: 'الكمية' })}>
                  <div className="qty-stepper">
                    <button
                      className="qty-btn"
                      onClick={() => void guardedQty(line, Math.max(line.product.allowDecimals ? Math.min(1, line.qty) : 1, line.qty - 1), () => changeQty(line.cartId, -1))}
                      title={tr({ en: 'Decrease', ar: 'إنقاص' })}
                    >
                      <MinusIcon size={14} />
                    </button>
                    <EditableQty
                      value={line.qty}
                      allowDecimals={!!line.product.allowDecimals}
                      onCommit={(n) => void guardedQty(line, n, () => setQty(line.cartId, n))}
                    />
                    <button
                      className="qty-btn"
                      onClick={() => changeQty(line.cartId, 1)}
                      title={tr({ en: 'Increase', ar: 'زيادة' })}
                    >
                      <PlusIcon size={14} />
                    </button>
                  </div>
                </div>

                {/* Discount — the line's own discount, read-only here; tapping
                    it opens the item-discount window for a cashier who may
                    discount (Inv_Receipt_d.Discount_Value). On a return line
                    it includes the original sale's discount and comes off the
                    refund. Never on a membership redemption or a gift card. */}
                <div className="ci-disc" data-label={tr({ en: 'Discount', ar: 'الخصم' })}>
                  {(() => {
                    const content = lineDisc > 0 ? (
                      <>
                        <span className="ci-disc-amt">−{fmt(lineDisc)}</span>
                        {!line.isReturn && linePromo === 0 && line.lineDiscount?.type === 'percent' && (
                          <span className="ci-disc-pct">{line.lineDiscount.value}%</span>
                        )}
                      </>
                    ) : (
                      <span className="ci-disc-none">—</span>
                    );
                    const title = [
                      tr({ en: 'Item discount', ar: 'خصم الصنف' }),
                      line.lineDiscount?.reason,
                      linePromo > 0 ? `${tr({ en: 'Offer', ar: 'عرض' })} −${fmt(linePromo)}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ');
                    return discountable ? (
                      <button
                        type="button"
                        className={`ci-box ci-disc-box ci-disc-box--edit${lineDisc > 0 ? ' has-disc' : ''}`}
                        onClick={() => setDiscountTargetId(line.cartId)}
                        title={title}
                        aria-label={tr({ en: 'Discount this item', ar: 'خصم على الصنف' })}
                      >
                        {content}
                      </button>
                    ) : (
                      <div className={`ci-box ci-disc-box${lineDisc > 0 ? ' has-disc' : ''}`} title={title}>
                        {content}
                      </div>
                    );
                  })()}
                </div>

                {/* Total — what the line is worth AFTER its discount, so the
                    column adds up to the Subtotal. Typing a total means that
                    after-discount amount (netToGross turns it back into the
                    price × qty the store keeps). */}
                <div className="ci-total" data-label={tr({ en: 'Total', ar: 'الإجمالي' })}>
                  <EditableTotal
                    value={lineNet}
                    editable={
                      canEditTotal &&
                      canEditCartItemPrice &&
                      // "No change allowed" hides the control rather than
                      // letting the cashier type into a box that refuses
                      // everything. It still permits the QUANTITY mode
                      // below, where the price never moves.
                      (priceRule !== 'none' || whenChangeTotal === 'quantity') &&
                      !line.membershipDetailId &&
                      !line.isReturn
                    }
                    // A typed total is after EVERYTHING shown, the offer included;
                    // the offer is the server's, so it is added back before the
                    // total becomes a price × qty.
                    onCommit={(n) => commitLineTotal(line, netToGross(line, n + linePromo))}
                  />
                </div>

                <div className="ci-remove">
                  <button
                    className="cart-item-remove"
                    onClick={() => void guardedQty(line, 0, () => removeLine(line.cartId))}
                    title={tr({ en: 'Remove item', ar: 'حذف الصنف' })}
                    aria-label={tr({ en: 'Remove item', ar: 'حذف الصنف' })}
                  >
                    <TrashIcon size={18} />
                  </button>
                </div>

                {((line.isReturn && line.product.restockOnReturn) ||
                  (!line.membershipDetailId && isSalesmanImplemented && salesmen.length > 0)) && (
                <div className="ci-extra">
                  {/* Restock decision — only for RETURN lines whose item is
                      flagged Inv_Items.RestockOnReturn. Ticked (the default)
                      is a plain return: the goods go back on the shelf.
                      Unticked makes the backend write a Code 61 Destruction
                      voucher alongside the return, so the customer is still
                      refunded but the stock is written off instead. */}
                  {line.isReturn && line.product.restockOnReturn && (
                    <label
                      className={`cart-item-restock${line.restock === false ? ' is-destroy' : ''}`}
                      title={
                        line.restock === false
                          ? tr({
                              en: 'Not restocked — a destruction voucher will be issued',
                              ar: 'لن يُعاد للمخزون — سيصدر سند إهلاك',
                            })
                          : tr({
                              en: 'Goods go back into stock',
                              ar: 'ترجع البضاعة إلى المخزون',
                            })
                      }
                    >
                      <input
                        type="checkbox"
                        checked={line.restock ?? true}
                        onChange={() => toggleLineRestock(line.cartId)}
                      />
                      <span>
                        {line.restock === false
                          ? tr({ en: 'Damaged — write off', ar: 'تالف — إهلاك' })
                          : tr({ en: 'Restock it', ar: 'إعادة للمخزون' })}
                      </span>
                    </label>
                  )}
                  {/* Per-line salesmen (Inv_Receipt_d.SalesManId / SalesMan2Id).
                      Two optional single-selects filled from Salesman-role users.
                      Salesman 2 stays disabled until Salesman 1 is picked, and
                      excludes whoever is chosen as #1. When the tenant mandates a
                      salesman, an empty #1 on a sold line is flagged red. Hidden
                      for prepaid membership lines and when no salesmen exist. */}
                  {!line.membershipDetailId && isSalesmanImplemented && salesmen.length > 0 && (
                    <div className="cart-item-salesmen">
                      <select
                        className={`cart-salesman-select${
                          isSalesmanMandatory && !line.isReturn && line.salesman1Id == null
                            ? ' is-required-missing'
                            : ''
                        }`}
                        value={line.salesman1Id ?? ''}
                        onChange={(e) =>
                          setLineSalesman(line.cartId, 1, e.target.value ? Number(e.target.value) : null)
                        }
                        title={tr({ en: 'Salesman', ar: 'المندوب' })}
                      >
                        <option value="">
                          {tr({ en: 'Salesman', ar: 'المندوب' })}
                          {isSalesmanMandatory && !line.isReturn ? ' *' : ''}
                        </option>
                        {salesmen.map((s) => (
                          <option key={s.id} value={s.id}>
                            {(tr({ en: s.name, ar: s.name_ar || s.name }) || s.name)}
                          </option>
                        ))}
                      </select>
                      {activateSecondSalesman && (
                        <select
                          className="cart-salesman-select"
                          value={line.salesman2Id ?? ''}
                          onChange={(e) =>
                            setLineSalesman(line.cartId, 2, e.target.value ? Number(e.target.value) : null)
                          }
                          disabled={line.salesman1Id == null}
                          title={tr({ en: 'Salesman 2 (optional)', ar: 'المندوب 2 (اختياري)' })}
                        >
                          <option value="">{tr({ en: 'Salesman 2', ar: 'المندوب 2' })}</option>
                          {salesmen
                            .filter((s) => s.id !== line.salesman1Id)
                            .map((s) => (
                              <option key={s.id} value={s.id}>
                                {(tr({ en: s.name, ar: s.name_ar || s.name }) || s.name)}
                              </option>
                            ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
                )}
              </div>
            );
          })}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="cart-footer">
        <div className="cart-summary">
          {/* A PURE return shows the refund's own figures in the rows a sale
              uses; an exchange keeps the sale's and adds a return-discount row. */}
          <div className="summary-row">
            <span>{t.mode === 'return' ? tr({ en: 'Returned items', ar: 'الأصناف المرتجعة' }) : tr({ en: 'Subtotal', ar: 'المجموع الفرعي' })}</span>
            {/* After the item-level offers, which the lines already show — so the
                Total column still adds up to this row. */}
            <span>{fmt(t.mode === 'return' ? t.returnSubtotal : t.subtotal - t.linePromotionTotal)}</span>
          </div>
          {t.mode === 'return' && t.returnDiscount > 0 && (
            <div className="summary-row discount">
              <span>{tr({ en: 'Discount', ar: 'الخصم' })}</span>
              <span>−{fmt(t.returnDiscount)}</span>
            </div>
          )}
          {t.mode !== 'return' && t.discount > 0 && (
            <div className="summary-row discount">
              <span>
                {tr({ en: 'Discount', ar: 'الخصم' })}
                {discount?.type === 'percent' ? ` ${discount.value}%` : ''}
                {discount?.reason ? ` · ${discount.reason}` : ''}
              </span>
              <span>−{fmt(t.discount)}</span>
            </div>
          )}
          {t.loyaltyDiscount + t.loyaltyPayment > 0 && (
            <div className="summary-row discount">
              <span>
                {tr({ en: 'Points', ar: 'النقاط' })}
                {loyaltyRedeem ? ` · ${loyaltyRedeem.points}` : ''}
              </span>
              <span>−{fmt(t.loyaltyDiscount + t.loyaltyPayment)}</span>
            </div>
          )}
          {/* Automatic promotions, NAMED. The cashier never applied these and
              cannot decline them, so an unexplained drop in the total would
              leave them nothing to tell the customer. One row per promotion. */}
          {t.promotions.map((p) => (
            <div className="summary-row discount" key={p.name}>
              <span>{lang === 'ar' && p.nameAr ? p.nameAr : p.name}</span>
              <span>−{fmt(p.amount)}</span>
            </div>
          ))}
          {t.deliveryFee > 0 && (
            <div className="summary-row">
              <span>{tr({ en: 'Delivery fee', ar: 'رسوم التوصيل' })}</span>
              <span>{fmt(t.deliveryFee)}</span>
            </div>
          )}
          <div className="summary-row">
            <span>{taxLabel} {taxPercent}%</span>
            <span>{fmt(t.mode === 'return' ? t.returnVat : t.vat)}</span>
          </div>
          {t.mode === 'exchange' && t.returnDiscount > 0 && (
            <div className="summary-row discount">
              <span>{tr({ en: 'Return discount', ar: 'خصم المرتجع' })}</span>
              <span>−{fmt(t.returnDiscount)}</span>
            </div>
          )}
          {t.hasReturns && (
            <div className="summary-row discount">
              <span>{tr({ en: 'Returned', ar: 'المرتجع' })}</span>
              <span>−{fmt(t.returnTotal)}</span>
            </div>
          )}
          <div className="summary-row total">
            <span>{t.hasReturns ? (t.net >= 0 ? tr({ en: 'Net to collect', ar: 'الصافي المطلوب تحصيله' }) : tr({ en: 'Net to refund', ar: 'الصافي المسترد' })) : tr({ en: 'Total', ar: 'الإجمالي' })}</span>
            <span>{fmt(Math.abs(t.total))}</span>
          </div>
        </div>

        <div className="cart-actions">
          <button
            className="btn-primary"
            onClick={openCharge}
            disabled={
              lines.length === 0 ||
              needsTable ||
              salesmanMissing ||
              // The basket changed and the server has not re-priced it yet, so
              // the amount on this button is not the amount the sale will be
              // for. Charging now collects the wrong money and the order is
              // rejected (PAYMENT_SUM_MISMATCH). It is one debounce long.
              promotionQuotePending ||
              (orderMode === 'delivery'
                ? false // gating happens inside the delivery modal
                : orderType === 'delivery' && !delivery)
            }
          >
            {orderMode === 'delivery'
              ? tr({ en: 'Create delivery', ar: 'إنشاء توصيل' })
              : t.hasReturns
                ? (t.net >= 0 ? tr({ en: 'Exchange', ar: 'استبدال' }) : tr({ en: 'Refund', ar: 'استرداد' }))
                : tr({ en: 'Charge', ar: 'تحصيل' })}{' '}
            <span>{fmt(Math.abs(t.total))}</span>
            <ArrowRightIcon size={18} />
          </button>
          <div className="btn-secondary-row">
            {/* IsHoldImplemented — the tenant-level gate for holding an order.
                A laundry tenant always has it: a NEW order is taken in here
                (ready-by date, then the ticket + bag tag print). */}
            {(isHoldImplemented || laundryOn) && (
              <button
                className="btn-secondary"
                onClick={() => (laundryOn && !editingHeld ? setReadyByOpen(true) : void holdOrder())}
                disabled={lines.length === 0 || holding}
                title={
                  editingHeld
                    ? tr({ en: 'Save the changes — it stays unpaid', ar: 'احفظ التغييرات — يبقى غير مدفوع' })
                    : tr({ en: 'Hold this order — pay it later', ar: 'علّق هذا الطلب — ادفعه لاحقاً' })
                }
              >
                <PauseIcon size={16} />
                {holding
                  ? tr({ en: 'Holding…', ar: 'جارٍ التعليق…' })
                  : laundryOn && !editingHeld
                    ? tr({ en: 'Take in', ar: 'استلام' })
                    : tr({ en: 'Hold', ar: 'تعليق' })}
              </button>
            )}
            {membershipDatesOpen && (
              <MembershipDatesDialog
                onCancel={() => setMembershipDatesOpen(false)}
                onConfirm={(dates) => {
                  useCart.getState().setMembershipDates(dates);
                  setMembershipDatesOpen(false);
                  setCartOpen(false);
                  showModal('payment');
                }}
              />
            )}
            {readyByOpen && (
              <ReadyByDialog
                busy={holding}
                onCancel={() => setReadyByOpen(false)}
                onConfirm={(readyBy) => void takeInLaundry(readyBy)}
                {...(orderMode !== 'delivery'
                  ? {
                      onPayNow: (readyBy: string) => {
                        // Paid at drop-off: the ordinary payment window, and the
                        // charge becomes a laundry order (usePlaceOrder).
                        useCart.getState().setLaundryPayNow({ readyBy });
                        setReadyByOpen(false);
                        setCartOpen(false);
                        showModal('payment');
                      },
                    }
                  : {})}
              />
            )}
            <button className="btn-secondary" onClick={requestDiscount}>
              <PercentIcon size={16} />
              {tr({ en: 'Discount', ar: 'الخصم' })}
            </button>
            {/* Empties the cart in one tap (with a confirm, so a mis-tap can't
                lose a full ticket). Not a "cancel order" — nothing is posted
                until Charge. */}
            <button
              className="btn-secondary"
              onClick={clearCart}
              disabled={lines.length === 0}
            >
              <TrashIcon size={16} />
              {tr({ en: 'Clear', ar: 'تفريغ' })}
            </button>
          </div>
        </div>
      </div>
      </aside>

      {/* Mobile-only floating bar. On narrow screens the catalog fills the
          width and the cart is hidden; this bar shows the running item count
          + total and slides the cart drawer in when tapped. Hidden on
          tablet/desktop. */}
      <button
        type="button"
        className="mobile-cart-bar"
        onClick={toggleCart}
        aria-label={tr({ en: 'View order', ar: 'عرض الطلب' })}
      >
        <span className="mobile-cart-bar-count">
          <ShoppingCartIcon size={18} />
          {itemCount > 0 && <span className="mobile-cart-bar-badge">{itemCount}</span>}
        </span>
        <span className="mobile-cart-bar-label">
          {itemCount === 0 ? tr({ en: 'Cart empty', ar: 'السلة فارغة' }) : tr({ en: 'View order', ar: 'عرض الطلب' })}
        </span>
        <span className="mobile-cart-bar-total">{fmt(t.total)}</span>
      </button>

      {discountTarget && (
        <LineDiscountModal
          // Remount per line so the inputs start from that line's own discount.
          key={discountTarget.cartId}
          line={discountTarget}
          onSave={(d) => setLineDiscount(discountTarget.cartId, d)}
          onClose={() => setDiscountTargetId(null)}
        />
      )}
      {noteTarget && (
        <LineNoteModal
          // Remount per line so the textarea starts from that line's own note.
          key={noteTarget.cartId}
          itemName={noteTarget.product.name}
          note={noteTarget.note ?? null}
          onSave={(next) => setLineNote(noteTarget.cartId, next)}
          onClose={() => setNoteTargetId(null)}
        />
      )}
      {restockOpen && (
        <RestockConfirmDialog
          lines={restockLines}
          onToggle={toggleLineRestock}
          onCancel={() => setRestockOpen(false)}
          onConfirm={() => {
            // Remember the answer for THIS set of lines, then fall back into
            // openCharge, which now walks past the gate to the payment modal.
            setRestockAskedFor(restockKey);
            setRestockOpen(false);
            setCartOpen(false);
            if (orderMode === 'delivery') showModal('deliveryConfirm');
            else showModal('payment');
          }}
        />
      )}
    </>
  );
}

/**
 * Line total cell. Read-only normally; when `editable` (admin flag
 * IsTotalPriceChanges) it becomes tap-to-edit: clicking turns it into a number
 * input, and committing (Enter / blur) calls `onCommit` with the new total. The
 * caller back-solves quantity or unit price per the WhenChangeTotal setting.
 */
/**
 * The quantity number in the +/- stepper, rendered as a click-to-edit field so
 * the cashier can type a quantity directly (e.g. items sold in packs of 100)
 * instead of clicking + repeatedly. Enter / blur commits; Escape cancels.
 *
 * `allowDecimals` (Inv_Items.AllowDecimals) opens the field to fractions —
 * 0.5, 0.25 — down to 0.001, for items sold by weight or length. Without it
 * the field stays whole units from 1, and a typed fraction is rounded to the
 * nearest unit rather than silently becoming 1.
 */
function EditableQty(props: {
  value: number;
  allowDecimals: boolean;
  onCommit: (qty: number) => void;
}): JSX.Element {
  const { value, allowDecimals, onCommit } = props;
  const { t: tr } = useLang();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (!editing) {
    return (
      <button
        type="button"
        className="qty-value qty-value--editable"
        title={tr({ en: 'Edit quantity', ar: 'تعديل الكمية' })}
        onClick={() => { setDraft(String(value)); setEditing(true); }}
      >
        {value}
      </button>
    );
  }

  const commit = (): void => {
    setEditing(false);
    // Accept the Arabic-keyboard decimal comma too.
    const n = parseFloat(draft.replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) return;
    onCommit(allowDecimals ? n : Math.max(1, Math.round(n)));
  };

  return (
    <input
      className="qty-value-input"
      type="number"
      inputMode="decimal"
      min={allowDecimals ? '0.001' : '1'}
      step={allowDecimals ? '0.001' : '1'}
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        else if (e.key === 'Escape') setEditing(false);
      }}
      onFocus={(e) => e.currentTarget.select()}
    />
  );
}

/**
 * A line's unit price, "@ 12.500", under its name — shown only to a cashier
 * who may change prices, and tap-to-edit for them. Enter / blur commits,
 * Escape cancels; the caller decides whether the cashier's ChangeSalesPriceMode
 * allows the new price.
 */
function EditableUnitPrice(props: {
  value: number;
  onCommit: (unitPrice: number) => void;
}): JSX.Element {
  const { value, onCommit } = props;
  const { t: tr } = useLang();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (!editing) {
    return (
      <button
        type="button"
        className="cart-item-unitprice"
        title={tr({ en: 'Edit unit price', ar: 'تعديل سعر الوحدة' })}
        onClick={() => { setDraft(amountText(value)); setEditing(true); }}
      >
        {fmt(value)}
      </button>
    );
  }

  const commit = (): void => {
    setEditing(false);
    // Accept the Arabic-keyboard decimal comma too. 0 is a price; empty or
    // negative is ignored.
    const n = parseFloat(draft.replace(',', '.'));
    if (Number.isFinite(n) && n >= 0 && n !== value) onCommit(n);
  };

  return (
    <input
      className="cart-item-unitprice-input"
      type="number"
      inputMode="decimal"
      min="0"
      step="any"
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        else if (e.key === 'Escape') setEditing(false);
      }}
      onFocus={(e) => e.currentTarget.select()}
    />
  );
}

/**
 * The Total column shows a line AFTER its own discount, but the store keeps
 * price × qty (setLineTotal back-solves the price or the quantity from it).
 * Turn a typed after-discount total back into that: an amount discount is
 * added back, a percent one divided out. A 100% discount leaves nothing to
 * divide by, so the typed figure is taken as price × qty, as before.
 */
export function netToGross(line: CartLine, net: number): number {
  const d = line.lineDiscount;
  if (!d || d.value <= 0) return net;
  if (d.type === 'amount') return net + d.value;
  if (d.value >= 100) return net;
  return net / (1 - d.value / 100);
}

function EditableTotal(props: {
  value: number;
  editable: boolean;
  onCommit: (newTotal: number) => void;
}): JSX.Element {
  const { value, editable, onCommit } = props;
  const { t: tr } = useLang();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (!editable) {
    return <div className="cart-item-price">{fmt(value)}</div>;
  }

  if (!editing) {
    return (
      <button
        type="button"
        className="cart-item-price cart-item-price--editable"
        title={tr({ en: 'Edit total', ar: 'تعديل الإجمالي' })}
        onClick={() => { setDraft(amountText(value)); setEditing(true); }}
      >
        {fmt(value)}
      </button>
    );
  }

  const commit = (): void => {
    setEditing(false);
    const n = parseFloat(draft);
    // 0 is a price the cashier may set — a comp, a giveaway, a replacement —
    // and it has to survive the commit or the cell reads as broken: it took
    // the keystrokes and then put the old amount back. An empty box (NaN) and
    // a negative are still ignored; what the cashier's ChangeSalesPriceMode
    // allows is decided in `commitLineTotal`, not here.
    if (Number.isFinite(n) && n >= 0) onCommit(n);
  };

  return (
    <input
      className="cart-item-price-input"
      type="number"
      inputMode="decimal"
      min="0"
      step={amountStep()}
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        else if (e.key === 'Escape') setEditing(false);
      }}
      onFocus={(e) => e.currentTarget.select()}
    />
  );
}
